import * as types from './actionTypes';
import { getComplianceCheckListForJob } from './complianceCheckListActions';

import { AxiosRequestConfig, AxiosResponse } from 'axios';
import {
    IcommissioningData,
    IcommissioningDataPayload,
    IinstallBase,
    Ijob,
    IjobWorkOrder,
    IMaterial,
    Ioption,
    Iproduct,
    ItableFiltersParams,
    IWorkOrder,
    JobNewInstallBases
} from '../models';
import debounce from 'lodash/debounce';
import filter from 'lodash/filter';
import keyBy from 'lodash/keyBy';
import map from 'lodash/map';
import omit from 'lodash/omit';
import pickBy from 'lodash/pickBy';
import values from 'lodash/values';

import API from '../constants/apiEndpoints';
import {
    applySubmittedLocationGroup,
    applySubmittedSystem,
    buildBulkInstallEffect
} from './manageInventoryActions.helpers';
import { IinitialState } from '../reducers';
import { addNewProductsFromInstallBases } from '../store/offlineProductsProvider';
import { TFunction } from 'i18next';
import { ThunkAction } from 'redux-thunk';
import { beginAjaxCall, endAjaxCall } from './ajaxStatusActions';
import { appSyncItemNames, constants } from '../constants/constants';
import { initialProduct, initialWorkOrder } from '../reducers/initialState';
import { msalFetch, acquireToken } from '../components/auth/Auth-Utils';
import { selectMainCategoryFromProductID } from '../reducers/manageInventoryReducer';
import { toastr } from 'react-redux-toastr';
import moment from 'moment';
import { AppSyncItemStatus, beaconContactTypeEnum, jobTypesIdEnum } from '../models-enums';
import { selectJobWorkOrdersForJobID } from '../reducers/commonSelectors';
import { getLatestHelper } from './commonActions';
import { getWorkOrders } from './workOrderActions';
import { FieldConfig } from 'react-reactive-form';
import { selectSelectedInstallBaseIDs } from '../reducers/commonReducers';
import { FormUtil } from '../components/common/FormUtil';
import localforage from 'localforage';
import {
    beginCatalogDownload,
    completeCatalogDownload,
    markCatalogDownloadFailed,
    markCatalogStage,
    responseChars
} from '../helpers/productSyncDiagnostics';
import {
    getAllProductsFromStore,
    getFilteredProductsFromStore,
    getProductByID,
    getProductsByIDs,
    saveProducts,
    saveMaterialsWithProgressTracking,
    materialStoreMigrationComplete
} from '../store/configureStore';
import { normalizeMaterialResponse, streamDtoToMaterial } from '../utils/materialMapper';
import { myWorker, resetProductSyncCounters } from './workerActions';

import { v4 as uuidv4 } from 'uuid';

type ThunkResult<R> = ThunkAction<R, IinitialState, undefined, any>;

// Store pending refresh parameters for when user comes back online
let pendingRefresh: { facilityID: string; lastSync: number; jobID: string } | null = null;

// Debounced function to prevent excessive getLatest calls during bulk operations
// Only executes if the user is online to avoid unnecessary offline queue buildup
const debouncedGetLatest = debounce(
    (dispatch: any, getState: any, facilityID: string, lastSync: number, jobID: string) => {
        // Check if user is still online before making API calls
        if (!getState().offline.online) {
            console.log('[debouncedGetLatest]: User is offline, storing refresh for when online');
            // Store the latest refresh parameters for when user comes back online
            pendingRefresh = { facilityID, lastSync, jobID };
            return;
        }

        // Clear any pending refresh since we're executing now
        pendingRefresh = null;

        console.log(`[debouncedGetLatest]: Executing refresh for facility ${facilityID}, job ${jobID}`);
        dispatch(getWorkOrders(facilityID));
        dispatch(getLatestHelper(lastSync, jobID));
    },
    2000
); // Wait 2 seconds after the last call before executing

// Function to execute pending refresh when user comes back online
export const executePendingRefresh = (): ThunkResult<any> => {
    return (dispatch, getState) => {
        if (pendingRefresh && getState().offline.online) {
            console.log('[executePendingRefresh]: Executing stored refresh after coming online');
            const { facilityID, lastSync, jobID } = pendingRefresh;
            pendingRefresh = null; // Clear the pending refresh

            dispatch(getWorkOrders(facilityID));
            dispatch(getLatestHelper(lastSync, jobID));
        }
    };
};

const cleanQuantity = (quantity: any): number => {
    // Make sure quantity is a number
    if (quantity === undefined || quantity === null || Number.isNaN(quantity)) {
        return 1;
    }

    if (typeof quantity === 'string') {
        return parseInt(quantity);
    }

    return quantity;
};

const cleanInstallBase = (installBase: IinstallBase) => ({
    ...installBase,
    materialID: installBase.materialID && installBase.materialID !== '' ? installBase.materialID : null,
    facilityID: installBase.facilityID && installBase.facilityID !== '' ? installBase.facilityID : null,
    buildingID: installBase.buildingID && installBase.buildingID !== '' ? installBase.buildingID : null,
    floorID: installBase.floorID && installBase.floorID !== '' ? installBase.floorID : null,
    locationID: installBase.locationID && installBase.locationID !== '' ? installBase.locationID : null,
    roomID: installBase.roomID && installBase.roomID !== '' ? installBase.roomID : null,
    workOrderID: installBase.workOrderID && installBase.workOrderID !== '' ? installBase.workOrderID : null,
    lastQuarterlyMaintenanceDate:
        installBase.lastQuarterlyMaintenanceDate === '' ? null : installBase.lastQuarterlyMaintenanceDate,
    lastSixMonthMaintenanceDate:
        installBase.lastSixMonthMaintenanceDate === '' ? null : installBase.lastSixMonthMaintenanceDate,
    lastYearlyMaintenanceDate:
        installBase.lastYearlyMaintenanceDate === '' ? null : installBase.lastYearlyMaintenanceDate,
    lastTwoYearMaintenanceDate:
        installBase.lastTwoYearMaintenanceDate === '' ? null : installBase.lastTwoYearMaintenanceDate,
    lastThreeYearMaintenanceDate:
        installBase.lastThreeYearMaintenanceDate === '' ? null : installBase.lastThreeYearMaintenanceDate,
    lastFiveYearMaintenanceDate:
        installBase.lastFiveYearMaintenanceDate === '' ? null : installBase.lastFiveYearMaintenanceDate,
    quantity: cleanQuantity(installBase.quantity),
    installDate: installBase.installDate === '' ? null : installBase.installDate,
    // Clean measurement point result IDs - convert empty strings to null
    latestAGSMeasurementPointListResultID:
        installBase.latestAGSMeasurementPointListResultID === ''
            ? null
            : installBase.latestAGSMeasurementPointListResultID,
    latestMeasurementPointListResultID:
        installBase.latestMeasurementPointListResultID === '' ? null : installBase.latestMeasurementPointListResultID,
    latestVerificationMeasurementPointListResultID:
        installBase.latestVerificationMeasurementPointListResultID === ''
            ? null
            : installBase.latestVerificationMeasurementPointListResultID,
    latestAuditMeasurementPointListResultID:
        installBase.latestAuditMeasurementPointListResultID === ''
            ? null
            : installBase.latestAuditMeasurementPointListResultID,
    latestCommissioningMeasurementPointListResultID:
        installBase.latestCommissioningMeasurementPointListResultID === ''
            ? null
            : installBase.latestCommissioningMeasurementPointListResultID,
    latestMaintenanceMeasurementPointListResultID:
        installBase.latestMaintenanceMeasurementPointListResultID === ''
            ? null
            : installBase.latestMaintenanceMeasurementPointListResultID,
    latestRepairMeasurementPointListResultID:
        installBase.latestRepairMeasurementPointListResultID === ''
            ? null
            : installBase.latestRepairMeasurementPointListResultID,
    latestServicePlanMeasurementPointListResultID:
        installBase.latestServicePlanMeasurementPointListResultID === ''
            ? null
            : installBase.latestServicePlanMeasurementPointListResultID,
    latestWarrantyMeasurementPointListResultID:
        installBase.latestWarrantyMeasurementPointListResultID === ''
            ? null
            : installBase.latestWarrantyMeasurementPointListResultID
});

export function updateVisibleProducts(allProducts?: { [key: string]: Iproduct }): ThunkResult<any> {
    return async (dispatch, getState) => {
        // to load products for all the facilities
        const { installBasesByID } = getState().manageInventory;

        // raises event for installbase products
        // triggers GET_ALL_PRODUCTS_SUCCESS for installbase products
        const installBasesArray = Object.values(installBasesByID);
        await addNewProductsFromInstallBases(installBasesArray);
    };
}

/*
 *  For offline we retrieve all the products from persistant storage, filter it, then bring only
 * the filtered products into Redux.  Otherwise there are too many and it crashes redux dev tools.
 */
export function searchProducts(
    page: number,
    search: string,
    mainCategoryID: string,
    subcategoryID: string,
    brandID: string,
    powerID: string,
    productTypeID: string,
    systemSizeID: string,
    isFinalProduct: boolean,
    productStandards: string[]
): ThunkResult<any> {
    return async (dispatch, getState) => {
        const searchString = search ? search.toLowerCase() : '';
        const { subcategories } = getState().productInfo;

        const filteredProducts = await getFilteredProductsFromStore({
            subcategoryID,
            mainCategoryID,
            searchString,
            brandID,
            powerID,
            productTypeID,
            systemSizeID,
            isFinalProduct,
            productStandards,
            subcategories
        });

        dispatch({
            type: types.SEARCH_PRODUCTS,
            products: filteredProducts.splice(0, constants.searchProductPageCount)
        });
    };
}

export async function getPagedProducts(chunkIndex: number, numChunks: number): Promise<void> {
    const authResponse = await acquireToken();
    if (!authResponse || !authResponse.accessToken) {
        throw new Error('failed to retrieve token. Likely because 3rd party cookies are disabled');
    }

    const token = authResponse.accessToken;
    if (!myWorker) {
        throw new Error('Worker not initialized');
    }

    const worker = myWorker;

    // Return a promise that resolves when this specific chunk completes
    return new Promise((resolve, reject) => {
        const handleMessage = (e: MessageEvent) => {
            if (e.data.action === 'getPagedProductsSuccess' && e.data.chunkIndex === chunkIndex) {
                worker.removeEventListener('message', handleMessage);
                resolve();
            } else if (e.data.action === 'getPagedProductsFailed' && e.data.chunkIndex === chunkIndex) {
                worker.removeEventListener('message', handleMessage);
                reject(new Error(`Failed to load chunk ${chunkIndex}`));
            }
        };

        worker.addEventListener('message', handleMessage);
        worker.postMessage({ action: 'getPagedProducts', token, chunkIndex, numChunks });
    });
}

/*
 * MMG-3788: Download products with delta support and weekly full refresh
 * - First sync (no delta key): full chunked download
 * - Subsequent syncs: delta download (material/stream?since=...)
 * - Weekly: force full download (if last full sync > 7 days)
 */
export function downloadProducts(trigger = 'sign-in'): ThunkResult<Promise<void>> {
    return async (dispatch, getState) => {
        try {
            await materialStoreMigrationComplete;

            dispatch({
                type: types.UPDATE_INITIAL_APP_SYNC_ITEM_STATUS,
                name: appSyncItemNames.Products,
                status: AppSyncItemStatus.inProgress
            });

            dispatch({
                type: types.UPDATE_SYNC_ITEM_RECORD_COUNT,
                name: appSyncItemNames.Products,
                recordCount: 1
            });

            const updateDateStr = await localforage.getItem('med-gas-mobile_all-products_delta');
            const lastFullSyncStr = await localforage.getItem('med-gas-mobile_all-products_full_sync');
            const fullSyncVersion = await localforage.getItem('med-gas-mobile_all-products_full_sync_v');

            // Determine if we need a full download
            let needsFullDownload = false;

            if (fullSyncVersion !== constants.productsFullSyncVersion) {
                needsFullDownload = true;
                console.log('[downloadProducts]: Full-sync version stale, forcing full download');
            } else if (!updateDateStr) {
                // No delta key: first sync, do full download
                needsFullDownload = true;
                console.log('[downloadProducts]: No delta key found, performing full download');
            } else {
                // We have a delta key - check if weekly full is required
                if (!lastFullSyncStr) {
                    // No full sync date recorded, force full download
                    needsFullDownload = true;
                    console.log('[downloadProducts]: No full sync date found, forcing full download');
                } else {
                    // Check if last full sync is older than 7 days
                    const lastFullSync = moment(lastFullSyncStr as string);
                    const daysSinceFullSync = moment().diff(lastFullSync, 'days');
                    
                    if (daysSinceFullSync > 7) {
                        needsFullDownload = true;
                        console.log(`[downloadProducts]: Last full sync was ${daysSinceFullSync} days ago (>7), forcing full download`);
                    } else {
                        console.log(`[downloadProducts]: Last full sync was ${daysSinceFullSync} days ago, performing delta download`);
                    }
                }
            }

            if (needsFullDownload) {
                if (getState().syncStatus.isBackgroundProductSyncRunning) {
                    console.log('[downloadProducts]: Full product download already running, skipping duplicate');
                    dispatch({ type: types.REMOVE_INITIAL_APP_SYNC_ITEM, name: appSyncItemNames.Products });
                    return Promise.resolve();
                }

                // Clear delta key if forcing full (so we start fresh)
                if (updateDateStr) {
                    await localforage.removeItem('med-gas-mobile_all-products_delta');
                    console.log('[downloadProducts]: Cleared delta key for full download');
                }

                // Set the background product sync running and failed to false
                dispatch({
                    type: types.SET_BACKGROUND_PRODUCT_SYNC_RUNNING,
                    backgroundProductSyncRunning: true
                });
                dispatch({
                    type: types.SET_BACKGROUND_PRODUCT_SYNC_FAILED,
                    backgroundProductSyncFailed: false
                });
                dispatch({
                    type: types.SET_BACKGROUND_PRODUCT_SYNC_FINISHED_COUNT,
                    backgroundProductSyncFinishedCount: 0
                });

                // Reset product sync counters before starting download
                resetProductSyncCounters();

                // Report estimated record count for fresh product download
                // Actual count will be updated as chunks report in
                dispatch({
                    type: types.UPDATE_SYNC_ITEM_RECORD_COUNT,
                    name: appSyncItemNames.Products,
                    recordCount: 50000 // Reasonable estimate for all products
                });

                // [deprecated] Load chunks sequentially to avoid IndexedDB contention (deprecated in favor of parallel loading)
                // await getPagedProducts(0, 4);
                // await getPagedProducts(1, 4);
                // await getPagedProducts(2, 4);
                // await getPagedProducts(3, 4);

                // MMG-4461: leave a trail so a phone that dies mid-download can report how far it got
                await beginCatalogDownload({
                    mode: 'full',
                    trigger,
                    numChunks: 4,
                    hadDeltaMarker: !!updateDateStr,
                    userEmail: getState().user?.email
                });

                // Load chunks in parallel
                await Promise.all([
                    getPagedProducts(0, 4),
                    getPagedProducts(1, 4),
                    getPagedProducts(2, 4),
                    getPagedProducts(3, 4)
                ]);
                // NOTE: Products sync item will be removed by worker when all 4 calls complete
            } else {
                // Perform delta download using ensureLatestProducts logic
                console.log('[downloadProducts]: Performing delta download');
                await dispatch(ensureLatestProducts(trigger));
            }

            return Promise.resolve();
        } catch (error) {
            console.error('[downloadProducts]:', error);
            await markCatalogDownloadFailed('full', error);
            dispatch({ type: types.REMOVE_INITIAL_APP_SYNC_ITEM, name: appSyncItemNames.Products });
            return Promise.reject(error);
        }
    };
}

export function getInventoryForJob(facilityID: string, jobID: string): ThunkResult<any> {
    return (dispatch, getState) => {
        let startTime: number;
        let lastLoaded = 0;
        let packetCount = 0;

        const url = API.inventory.getallinstallsforjob;
        const pagingType = 'None';

        const axiosOptions: AxiosRequestConfig = {
            method: 'get',
            params: { jobID, facilityID, pagingType },
            onDownloadProgress: progressEvent => {
                const currentTime = new Date().getTime();
                if (!startTime) {
                    startTime = currentTime;
                }
                const timeElapsedInSeconds = (currentTime - startTime) / 1000;
                const loadedBits = progressEvent.loaded * 8 - lastLoaded;
                const speedInBps = loadedBits / timeElapsedInSeconds;
                const speedInMbps = speedInBps / 1_000_000;

                if (packetCount % 10 === 0) {
                    dispatch({ type: types.SET_DOWNLOAD_SPEED, downloadSpeed: speedInMbps.toFixed(2) });
                }

                packetCount++;
                lastLoaded = progressEvent.loaded;
            }
        };

        return msalFetch(url, axiosOptions)
            .then(async (data: AxiosResponse<any>) => {
                if (!data.data) {
                    dispatch({
                        type: types.UPDATE_INITIAL_APP_SYNC_ITEM_STATUS,
                        name: appSyncItemNames.Inventory,
                        status: AppSyncItemStatus.failed
                    });
                } else {
                    dispatch({
                        type: types.GET_INVENTORY_SUCCESS,
                        installBases: data.data,
                        facilityID
                    });

                    // Load products from IndexedDB for all install bases
                    const installBases = data.data;
                    const uniqueMaterialIDs = [...new Set(installBases.map((ib: any) => ib.materialID).filter(Boolean))] as string[];
                    
                    if (uniqueMaterialIDs.length > 0) {
                        const products = await getProductsByIDs(uniqueMaterialIDs);
                        const productsArray = Object.values(products);
                        
                        if (productsArray.length > 0) {
                            dispatch({
                                type: types.GET_ALL_PRODUCTS_SUCCESS,
                                payload: productsArray
                            });
                        }
                    }

                    dispatch({ type: types.REMOVE_INITIAL_APP_SYNC_ITEM, name: appSyncItemNames.Inventory });
                }
            })
            .catch((error: any) => {
                dispatch({ type: types.GET_INVENTORY_FAILED });
                constants.handleError(error, 'get inventory', 'error', false);
                console.error('[getInventory]:', error);

                dispatch({
                    type: types.UPDATE_INITIAL_APP_SYNC_ITEM_STATUS,
                    name: appSyncItemNames.Inventory,
                    status: AppSyncItemStatus.failed
                });
            });
    };
}

export function getInventory(givenID?: string, skipQueue = false): ThunkResult<any> {
    return (dispatch, getState) => {
        let jobID = getState().manageJob.selectedJob.id;
        let facilityID = givenID ? givenID : getState().manageJob.selectedJob.facilityID;

        if (jobID === '' || facilityID === '') {
            console.error(`getInventory - jobID [${jobID}] or facilityID [${facilityID}] is empty`);
            return Promise.resolve(true);
        }

        const url = API.inventory.getallinstallsforjob;
        const pagingType = 'None';

        if (skipQueue) {
            const axiosOptions: AxiosRequestConfig = {
                method: 'get',
                params: { jobID, facilityID, pagingType }
            };

            return msalFetch(url, axiosOptions)
                .then((data: AxiosResponse<any>) => {
                    if (!data.data) {
                        throw new Error('error getting inventory');
                    } else {
                        dispatch({
                            type: types.GET_INVENTORY_SUCCESS,
                            installBases: data.data,
                            facilityID
                        });
                    }
                })
                .catch((error: any) => {
                    dispatch({ type: types.GET_INVENTORY_FAILED });
                    constants.handleError(error, 'get inventory', 'error', false);
                    console.error('[getInventory]:', error);
                    throw error;
                });
        } else {
            const axiosOptions: AxiosRequestConfig = {
                method: 'get',
                params: { jobID, facilityID, pagingType },
                url
            };

            dispatch({
                type: types.GET_INVENTORY,
                meta: {
                    offline: {
                        effect: { axiosOptions, message: 'Get Inventory for Facility' },
                        commit: {
                            type: types.GET_INVENTORY_SUCCESS,
                            facilityID
                        }
                    }
                }
            });

            return Promise.resolve(true);
        }
    };
}

export function getReportPreview(givenID?: string): ThunkResult<any> {
    return (dispatch, getState) => {
        const jobID = givenID;
        dispatch(beginAjaxCall());
        const axiosOptions: AxiosRequestConfig = {
            method: 'GET',
            params: {
                jobID
            }
        };

        const url = API.GET.report.preview;

        return msalFetch(url, axiosOptions)
            .then((data: AxiosResponse<any>) => {
                toastr.success('Running report preview.', 'Report Preview');
                dispatch(endAjaxCall());
            })
            .catch((error: any) => {
                dispatch(endAjaxCall());
                constants.handleError(error, 'get report preview');
                console.error('[getReportPreview]:', error);
            });
    };
}

/*
 * MMG-4513: whether Final Check should ask "Include Deficiency / Repair page in report?" on this repair job.
 * Rejects on failure; the caller decides what to show.
 */
export const getDeficiencyChapterApplies = (jobID: string): Promise<boolean> =>
    msalFetch(API.GET.report.deficiencyChapterApplies, {
        method: 'get',
        params: { jobID },
        timeout: constants.httpTimeout
    }).then((response: AxiosResponse<{ applies?: unknown }>) => {
        const applies = response.data?.applies;
        if (typeof applies !== 'boolean') {
            throw new Error('DeficiencyChapterApplies returned no answer');
        }
        return applies;
    });

/*
 * MMG-4296/4297: in-browser report preview. Fetches the PDF blob from the existing
 * PreviewInline endpoint and resolves with an object URL — it deliberately does NOT
 * open or write to a window, so the caller keeps ownership of progress and failure
 * feedback (see JobFinalCheckModal, which keeps both on the Final Check modal).
 * Resolves undefined when there is nothing to show; the reason is surfaced by toastr.
 * Respects the live "Include NOT TESTED Assets?" (and MMG-4513 Deficiency / Repair page) answers and does NOT close the job,
 * so the FSE can preview repeatedly before closing.
 */
export function getReportPreviewInline(
    jobID?: string,
    includeNotTestedAssets = true,
    includeDeficiencyChapters?: boolean
): ThunkResult<any> {
    return dispatch => {
        dispatch(beginAjaxCall());
        // Drives the header CubeLoader for the whole render. beginAjaxCall alone is not
        // enough: any unrelated _SUCCESS/_FAILED action decrements the shared ajax counter
        // back to zero and the loader vanishes mid-call.
        dispatch({ type: types.SET_REPORT_PREVIEW_GENERATING, payload: true });
        const axiosOptions: AxiosRequestConfig = {
            method: 'get',
            params: {
                jobID,
                includeNotTestedAssets,
                ...(includeDeficiencyChapters === undefined ? {} : { includeDeficiencyChapters })
            },
            responseType: 'blob',
            // Telerik rendering a full report routinely outlives the global 60s default
            // that the sync flows install on Axios. Web passes the same download timeout
            // for this endpoint; without it a slow report aborts as a bare timeout.
            timeout: constants.httpTimeoutDownload
        };

        return msalFetch(API.GET.report.previewInline, axiosOptions)
            .then((data: AxiosResponse<any>) => {
                dispatch(endAjaxCall());
                dispatch({ type: types.SET_REPORT_PREVIEW_GENERATING, payload: false });
                const blob: Blob | undefined = data.data;
                // A 204/empty body (report engine produced no PDF) comes back as a
                // zero-length blob, which is still truthy — treat it as "no preview".
                // The underlying reason is only in the API log (BuildCustomReport).
                if (!blob || blob.size === 0) {
                    toastr.warning('Report Preview', 'No report preview is available for this job.', constants.toastrWarning);
                    return undefined;
                }

                return window.URL.createObjectURL(blob);
            })
            .catch((error: any) => {
                dispatch(endAjaxCall());
                dispatch({ type: types.SET_REPORT_PREVIEW_GENERATING, payload: false });
                constants.handleError(error, 'preview report');
                console.error('[getReportPreviewInline]:', error);
                return undefined;
            });
    };
}

/*
 ! This is not used currently in the offline app
* update the product in persistent storage and Redux if it is there.
*/
// export function updateProduct(product: Iproduct): ThunkResult<any> {
//     return (dispatch, getState) => {
//         offlineProductsProvider
//             .getAllProducts()
//             .then((products: { [key: string]: Iproduct }) => {
//                 // dispatch({
//                 //   type: types.PRODUCT_UPDATE,
//                 //   product,
//                 //   meta: {
//                 //     offline: {
//                 //       effect: { axiosOptions, message: 'update product' },
//                 //       rollback: { type: types.PRODUCT_UPDATE, product: originalProduct } // TODO this rollback will not effect persistent storage
//                 //     }
//                 //   }
//                 // });
//                 const updatedProducts = mapValues(products, (prod: Iproduct) => {
//                     if (prod.id === product.id) {
//                         return { ...prod, ...product };
//                     } else {
//                         return prod;
//                     }
//                 });
//                 return offlineProductsProvider.setAllProducts(updatedProducts);
//             })
//             .catch((error: any) => console.error('[updateProduct]:', error));
//     };
// }

export const updatePersistedProducts = (updatedProducts: Iproduct[]) => {
    return saveProducts(updatedProducts).then(() => {
        // let the inventory view know to update the view with new products.  Have to do this here because products are not in redux due to their size.
        const event = new CustomEvent('updatedProducts');
        document.dispatchEvent(event);
    });
};

/*
 * save (add) a new product to persistent storage only.  No need to update Redux because there are no installs associated with the product yet.
 */
export function saveProduct(product: Iproduct): ThunkResult<any> {
    return (dispatch, getState) => {
        dispatch({ type: types.TOGGLE_MODAL_EDIT_PRODUCT });
        // Naming conflict with saveProduct, since this method is saveProduct, so just use saveProducts instead
        saveProducts([product]);

        const axiosOptions = {
            url: API.inventory.addproduct,
            method: 'post',
            data: product
        };
        dispatch({
            type: types.PRODUCT_ADD_SUCCESS,
            product,
            meta: {
                offline: {
                    effect: { axiosOptions, message: 'add new product' }
                    // rollback: { type: types.PRODUCT_ADD_SUCCESS, product: product }
                    // We could make a dummy reducer that dispatches an event that updates the product in offlineProductsProvider to be deleted
                    // Could extend rollback to receive a callback function (and call it)
                }
            }
        });
    };
}

export function updateInstall(install: IinstallBase, t: TFunction, fullObject = false): ThunkResult<any> {
    return (dispatch, getState) => {
        dispatch({ type: types.CLOSE_ALL_MODALS });
        const originalInstallBase = getState().manageInventory.installBasesByID[install.id];
        const axiosOptions = {
            url: API.inventory.update,
            method: 'post',
            data: cleanInstallBase(install)
        };
        dispatch({
            type: types.INSTALL_UPDATE,
            install,
            fullObject,
            meta: {
                offline: {
                    effect: { axiosOptions, message: 'update install' },
                    rollback: {
                        type: types.INSTALL_UPDATE,
                        install: originalInstallBase
                    }
                }
            }
        });
        document.dispatchEvent(new CustomEvent('updatedInstallBases'));
    };
}

export function addOrUpdateFacilityContact(facilityContact: any, facilityID: string, isAdd: boolean): ThunkResult<any> {
    return dispatch => {
        const { id } = facilityContact;
        const url = !id ? API.POST.contact.add : API.POST.contact.update;
        const method = !id ? 'POST' : 'PUT';

        if (!id) {
            const newId = uuidv4();
            facilityContact.id = newId;
            facilityContact.contactFacilities.forEach((cf: any) => (cf.contactID = newId));
        }

        const axiosOptions = {
            url,
            method,
            data: facilityContact
        };

        dispatch({
            type: isAdd ? types.ADD_FACILITY_CONTACT : types.UPDATE_FACILITY_CONTACT,
            payload: { ...facilityContact, facilityID },
            meta: {
                offline: {
                    effect: { axiosOptions }
                }
            }
        });
    };
}

export function deleteFacilityContact(facilityContact: any, facilityID: string): ThunkResult<any> {
    return dispatch => {
        const { id } = facilityContact;

        const axiosOptions = {
            url: API.POST.contact.delete(id),
            method: 'POST',
            params: FormUtil.toUrlSearchParams({ facilityId: facilityID })
        };

        dispatch({
            type: types.DELETE_FACILITY_CONTACT,
            payload: { facilityContactID: id, facilityID },
            meta: {
                offline: {
                    effect: { axiosOptions }
                }
            }
        });
    };
}

export function bulkUpdateInstalls(formValues: { [key: string]: any }): ThunkResult<any> {
    return (dispatch, getState) => {
        dispatch({ type: types.CLOSE_ALL_MODALS });
        const { selection, installBasesByID } = getState().manageInventory;
        const selectedInstallBaseIDs = selection.map(item => {
            return item.split('select-')[1];
        });
        const installBasesToUpdate = filter(installBasesByID, (install: IinstallBase) => {
            let shouldInclude = true;
            if (selectedInstallBaseIDs.indexOf(install.id) === -1) {
                shouldInclude = false;
            }
            if (install.isDeleted === true) {
                shouldInclude = false;
            }
            return shouldInclude;
        });

        // grab the inputs where the user actually filled something out
        let definedFormValues = pickBy(
            formValues,
            (property: any) => property !== null && property !== undefined && property && property.length !== 0
        );

        // in order to avoid moving an asset to a location where a room does not exist for instance, add back empty child location IDs that are not defined as part of the change
        if (definedFormValues.locationID && !definedFormValues.roomID) {
            definedFormValues.roomID = null;
        }
        if (definedFormValues.floorID && !definedFormValues.locationID) {
            definedFormValues.locationID = null;
        }
        if (definedFormValues.buildingID && !definedFormValues.floorID) {
            definedFormValues.floorID = null;
        }

        // MMG-4200: a single-selection edit is pre-filled from the asset, so the form's
        // location group is authoritative — a cleared location must persist.
        // MMG-4442: scoped to the keys the caller actually submitted, so a partial bulk
        // mutation (Change Product, delete) no longer wipes the asset's location.
        if (installBasesToUpdate.length === 1) {
            definedFormValues = applySubmittedLocationGroup(definedFormValues, formValues);
            definedFormValues = applySubmittedSystem(definedFormValues, formValues);
        }

        // update the installs
        // TODO when changing a parent location ID, need to reset the child location IDs
        const updatedInstalls = map(installBasesToUpdate, (install: IinstallBase) => {
            // If any of these fields are an empty string, the backend will blow up
            let tempInstall: any = { ...install, ...definedFormValues };
            tempInstall.lastFiveYearMaintenanceDate =
                tempInstall.lastFiveYearMaintenanceDate === '' ? null : tempInstall.lastFiveYearMaintenanceDate;
            tempInstall.lastThreeYearMaintenanceDate =
                tempInstall.lastThreeYearMaintenanceDate === '' ? null : tempInstall.lastThreeYearMaintenanceDate;
            tempInstall.lastTwoYearMaintenanceDate =
                tempInstall.lastTwoYearMaintenanceDate === '' ? null : tempInstall.lastTwoYearMaintenanceDate;
            tempInstall.lastYearlyMaintenanceDate =
                tempInstall.lastYearlyMaintenanceDate === '' ? null : tempInstall.lastYearlyMaintenanceDate;
            tempInstall.lastSixMonthMaintenanceDate =
                tempInstall.lastSixMonthMaintenanceDate === '' ? null : tempInstall.lastSixMonthMaintenanceDate;
            tempInstall.lastQuarterlyMaintenanceDate =
                tempInstall.lastQuarterlyMaintenanceDate === '' ? null : tempInstall.lastQuarterlyMaintenanceDate;

            tempInstall.latestAGSMeasurementPointListResultID =
                tempInstall.latestAGSMeasurementPointListResultID === ''
                    ? null
                    : tempInstall.latestAGSMeasurementPointListResultID;
            tempInstall.latestAuditMeasurementPointListResultID =
                tempInstall.latestAuditMeasurementPointListResultID === ''
                    ? null
                    : tempInstall.latestAuditMeasurementPointListResultID;
            tempInstall.latestCommissioningMeasurementPointListResultID =
                tempInstall.latestCommissioningMeasurementPointListResultID === ''
                    ? null
                    : tempInstall.latestCommissioningMeasurementPointListResultID;
            tempInstall.latestMeasurementPointListResultID =
                tempInstall.latestMeasurementPointListResultID === ''
                    ? null
                    : tempInstall.latestMeasurementPointListResultID;
            tempInstall.latestVerificationMeasurementPointListResultID =
                tempInstall.latestVerificationMeasurementPointListResultID === ''
                    ? null
                    : tempInstall.latestVerificationMeasurementPointListResultID;
            tempInstall.latestMaintenanceMeasurementPointListResultID =
                tempInstall.latestMaintenanceMeasurementPointListResultID === ''
                    ? null
                    : tempInstall.latestMaintenanceMeasurementPointListResultID;
            tempInstall.latestRepairMeasurementPointListResultID =
                tempInstall.latestRepairMeasurementPointListResultID === ''
                    ? null
                    : tempInstall.latestRepairMeasurementPointListResultID;
            tempInstall.latestServicePlanMeasurementPointListResultID =
                tempInstall.latestServicePlanMeasurementPointListResultID === ''
                    ? null
                    : tempInstall.latestServicePlanMeasurementPointListResultID;
            tempInstall.latestWarrantyMeasurementPointListResultID =
                tempInstall.latestWarrantyMeasurementPointListResultID === ''
                    ? null
                    : tempInstall.latestWarrantyMeasurementPointListResultID;

            tempInstall.buildingID = tempInstall.buildingID === '' ? null : tempInstall.buildingID;
            tempInstall.floorID = tempInstall.floorID === '' ? null : tempInstall.floorID;
            tempInstall.locationID = tempInstall.locationID === '' ? null : tempInstall.locationID;
            tempInstall.roomID = tempInstall.roomID === '' ? null : tempInstall.roomID;
            tempInstall.workOrderID = tempInstall.workOrderID === '' ? null : tempInstall.workOrderID;

            tempInstall.quantity = cleanQuantity(tempInstall.quantity);

            return tempInstall;
        });

        // we need to bulk delete the work orders if install is deleted
        let workOrdersForDeletedInstalls: IWorkOrder[] = [];
        let optimisticallyDeletedWorkOrders: IWorkOrder[] = [];

        if (updatedInstalls[0]?.isDeleted) {
            updatedInstalls.forEach((install: IinstallBase) => {
                workOrdersForDeletedInstalls = [
                    ...workOrdersForDeletedInstalls,
                    ...filter(getState().workOrder.workOrdersByID, (wo: IWorkOrder) => wo.installBaseID === install.id)
                ];
            });
            if (workOrdersForDeletedInstalls && workOrdersForDeletedInstalls.length > 0) {
                optimisticallyDeletedWorkOrders = map(workOrdersForDeletedInstalls, (workOrder: IWorkOrder) => ({
                    ...workOrder,
                    isDeleted: true
                }));
            }
        }

        // A bulk delete is the only mutation that must route through the dedicated
        // BulkDelete endpoint: it soft-deletes by ID only (no full-object Verify()),
        // so one invalid asset in the selection can no longer 412 and silently abort
        // the whole batch the way UpdateInstalls does. Non-delete bulk edits still PUT
        // UpdateInstalls. Mirrors the Web dashboard, which already uses BulkDelete.
        const isBulkDelete = definedFormValues.isDeleted === true;
        const axiosOptions = buildBulkInstallEffect(isBulkDelete, updatedInstalls);
        dispatch({
            type: types.INSTALL_UPDATE_BULK,
            installs: updatedInstalls,
            workOrders: optimisticallyDeletedWorkOrders,
            meta: {
                offline: {
                    effect: { axiosOptions, message: isBulkDelete ? 'bulk delete installs' : 'update install' },
                    rollback: {
                        type: types.INSTALL_UPDATE_BULK,
                        installs: installBasesToUpdate,
                        workOrders: workOrdersForDeletedInstalls
                    }
                }
            }
        });
        document.dispatchEvent(new CustomEvent('updatedInstallBases'));
    };
}

/*
 * prepare for updating the product on the selected installs
 * get the installs
 * check that all the installs are the same category
 * pre-select the category
 * disable the category input
 */
export function startBulkUpdateInstallProduct(t: TFunction): ThunkResult<any> {
    return (dispatch, getState) => {
        const { selection, installBasesByID } = getState().manageInventory;
        const selectedInstallBaseIDs = selection.map(item => {
            return item.split('select-')[1];
        });
        const installBasesToUpdate = filter(installBasesByID, (install: IinstallBase) => {
            let shouldInclude = true;
            if (selectedInstallBaseIDs.indexOf(install.id) === -1) {
                shouldInclude = false;
            }
            if (install.isDeleted === true) {
                shouldInclude = false;
            }
            return shouldInclude;
        });

        if (installBasesToUpdate.length === 0) {
            toastr.warning(t('toastMessage:warning'), t('toastMessage:mustSelectAtLeastOne'), constants.toastrWarning);
            return;
        }

        const firstInstall = installBasesToUpdate[0];
        if (!firstInstall) {
            toastr.warning(t('toastMessage:warning'), t('toastMessage:mustSelectAtLeastOne'), constants.toastrWarning);
            return;
        }

        const firstCategory = selectMainCategoryFromProductID(getState(), {
            materialID: firstInstall.materialID
        });

        const validCategory =
            filter(installBasesToUpdate, (install: IinstallBase) => {
                const category = selectMainCategoryFromProductID(getState(), {
                    materialID: install.materialID
                });
                return category.id !== firstCategory.id;
            }).length === 0;

        if (validCategory === false) {
            toastr.warning(
                t('toastMessage:warning'),
                t('toastMessage:mustSelectSameCategory'),
                constants.toastrWarning
            );
            return;
        }

        dispatch({
            type: types.SET_INSTALL_BATCH_CATEGORY,
            payload: firstCategory.id
        });
        dispatch({
            type: types.SET_INSTALL_BATCH_MODE,
            payload: true
        });
        dispatch({
            type: types.TOGGLE_MODAL_SEARCH_NEW_PRODUCTS
        });
    };
}

export const saveInstallsHelper = (
    installBases: { [key: string]: IinstallBase },
    t: TFunction,
    lastSync: number,
    product?: Iproduct,
    job?: Ijob,
    originalJobWorkOrders?: IjobWorkOrder[],
    isCloning = false
): ThunkResult<any> => {
    return (dispatch, getState) => {
        dispatch(beginAjaxCall());

        let newWorkOrders: IWorkOrder[] = [];
        let newJobWorkOrders: IjobWorkOrder[] = [];
        const jobID = job ? job.id : '';

        // TODO make a helper function (isRepairFlow) to determine if we are in a repair flow
        // MMG-4433: commissioning included - the server already creates the WO + link for it
        if (
            job &&
            (job.jobTypeID === jobTypesIdEnum.repair ||
                job.jobTypeID === jobTypesIdEnum.maintenance ||
                job.jobTypeID === jobTypesIdEnum.warrantyBM ||
                job.jobTypeID === jobTypesIdEnum.servicePlan ||
                job.jobTypeID === jobTypesIdEnum.commissioning) &&
            originalJobWorkOrders
        ) {
            // Loop through each install
            Object.values(installBases).forEach(install => {
                const workOrderID = install.workOrderID ? install.workOrderID : uuidv4();
                const installBase = installBases[install.id];
                if (installBase) {
                    installBase.workOrderID = workOrderID;
                }

                const newWorkOrder: IWorkOrder = {
                    ...initialWorkOrder,
                    id: workOrderID,
                    dueDate: moment.utc().format(constants.momentSQLFormat),
                    installBaseID: install.id
                };
                const newJobWorkOrder: IjobWorkOrder = {
                    jobID,
                    workOrderID,
                    isDeleted: false,
                    id: uuidv4(),
                    workOrder: newWorkOrder
                };

                if (installBase) {
                    installBase.jobWorkorderID = newJobWorkOrder.id;
                }

                newWorkOrders.push(newWorkOrder);
                newJobWorkOrders.push(newJobWorkOrder);
            });
        }

        // const installBasesForRollback = map(installBases, install => ({
        //     ...install,
        //     isDeleted: true
        // }));

        const cleanInstalls = values(installBases).map((install: IinstallBase) => {
            let cleaned: any = cleanInstallBase(install);

            // Make sure quantity is a number
            if (cleaned.quantity === undefined || cleaned.quantity === null || Number.isNaN(cleaned.quantity)) {
                cleaned.quantity = 1;
            }

            if (typeof cleaned.quantity === 'string') {
                cleaned.quantity = parseInt(cleaned.quantity);
            }

            // Backend will freak out if you pass an empty string for these fields
            return omit(
                cleaned,
                'latestAGSMeasurementPointListResultID',
                'latestAuditMeasurementPointListResultID',
                'latestCommissioningMeasurementPointListResultID',
                'latestMeasurementPointListResultID',
                'latestVerificationMeasurementPointListResultID',
                'latestMaintenanceMeasurementPointListResultID',
                'latestRepairMeasurementPointListResultID',
                'latestServicePlanMeasurementPointListResultID',
                'latestWarrantyMeasurementPointListResultID'
            );
        });
        const axiosOptions = {
            url: API.inventory.addinstall,
            method: 'post',
            data: { JobID: jobID, InstallBases: cleanInstalls, Cloning: isCloning }
        };

        // Generate a unique action ID to prevent duplicate processing
        const actionId = uuidv4();

        const offlineConfig: any = {
            effect: { axiosOptions, message: 'add install' },
            rollback: {
                type: types.INSTALL_ADD_ROLLBACK,
                installIds: Object.keys(installBases),
                actionId // Include actionId in rollback for proper cleanup
            }
        };

        // For cloning operations, add commit action after successful API call
        if (isCloning) {
            offlineConfig.commit = {
                type: types.INSTALL_ADD_SUCCESS,
                isCloning: true,
                facilityID: job?.facilityID,
                installIds: Object.keys(installBases),
                actionId
            };
        }

        dispatch({
            type: types.INSTALL_ADD,
            installs: installBases,
            product,
            actionId, // Add unique identifier to track this specific action
            isCloning, // Pass cloning flag for commit action
            facilityID: job?.facilityID,
            installIds: Object.keys(installBases),
            meta: {
                offline: offlineConfig
            }
        });

        // Save new work orders to the store
        if (newWorkOrders && newWorkOrders.length > 0) {
            newWorkOrders.forEach(wo => {
                dispatch({
                    type: types.ADD_WORKORDER,
                    workOrder: wo
                });
            });
        }

        // Save new job work orders to the store
        if (newJobWorkOrders && newJobWorkOrders.length > 0) {
            let jobWorkOrdersToSave: IjobWorkOrder[] = [...newJobWorkOrders];
            if (originalJobWorkOrders) {
                jobWorkOrdersToSave = [...originalJobWorkOrders, ...newJobWorkOrders];
            }

            dispatch({
                type: types.JOB_UPDATE_WORKORDERS,
                jobWorkOrders: jobWorkOrdersToSave
            });
        }

        // Since we are setting a timeout below, keep the spinning indicator going until the logic in the timeout is complete
        // Other wise the user will think the app is done loading only for it to kick off more stuff shortly after
        dispatch(beginAjaxCall());

        // Use different refresh logic for cloning vs normal operations
        if (job) {
            const installCount = Object.keys(installBases).length;

            if (isCloning) {
                // For cloning, we need to wait for the API call to complete before refreshing
                // The refresh will be handled by the INSTALL_ADD_SUCCESS action
                // Just end the ajax call after a short delay to allow the offline middleware to process
                setTimeout(() => {
                    dispatch(endAjaxCall());
                }, 500);
            } else {
                console.log(
                    `[saveInstallsHelper]: Normal operation - scheduling debounced getLatest for ${installCount} install bases`
                );
                // Call the debounced function - it will only execute after 2 seconds of no new calls
                // and only if the user is still online
                debouncedGetLatest(dispatch, getState, job.facilityID, lastSync, job.id);

                /*
                 * Adding an asset seeds its outstanding deficiencies server-side, and the catch-up
                 * sync above does not carry checklist data - without this the engineer would not see
                 * them until they left the job and opened it again.
                 */
                if (getState().offline.online) {
                    dispatch(getComplianceCheckListForJob(job.id));
                }
                // Still need to end the ajax call for this specific operation
                setTimeout(() => {
                    dispatch(endAjaxCall());
                }, 500);
            }
        } else {
            dispatch(endAjaxCall());
        }

        toastr.success(t('toastMessage:success'), t('toastMessage:savedInstall'), constants.toastrSuccess);
        document.dispatchEvent(new CustomEvent('updatedInstallBases'));
    };
};

/*
 * save (add) an install (an install might have a quantity greater than 1 so we might be adding multiple installs)
 */
export function saveInstall(install: IinstallBase, t: TFunction, product?: Iproduct): ThunkResult<any> {
    return (dispatch, getState) => {
        dispatch({ type: types.CLOSE_ALL_MODALS });

        // if quantity is greater than 1, we are creating multiple installs
        const newID = uuidv4();
        const job = getState().manageJob.selectedJob;
        const { lastSync } = getState().syncStatus;
        const originalJobWorkOrders = selectJobWorkOrdersForJobID(getState(), {
            jobID: job.id
        });
        let newInstalls = {};

        if (install.quantity && install.quantity > 1) {
            for (let i = install.quantity - 1; i >= 0; i--) {
                const newIDb = uuidv4();
                newInstalls = {
                    ...newInstalls,
                    [newIDb]: { ...install, id: newIDb, quantity: 1 }
                };
            }
        } else {
            newInstalls = { [newID]: { ...install, id: newID } };
        }
        dispatch(saveInstallsHelper(newInstalls, t, lastSync, product, job, originalJobWorkOrders));
    };
}

export function deleteInstall(installBaseID: string): ThunkResult<any> {
    return (dispatch, getState) => {
        const originalInstallBase = getState().manageInventory.installBasesByID[installBaseID];

        const workOrdersForDeletedInstall = filter(
            getState().workOrder.workOrdersByID,
            wo => wo.installBaseID === installBaseID
        );
        const optimisticallyDeletedWorkOrders = map(workOrdersForDeletedInstall, workOrder => ({
            ...workOrder,
            isDeleted: true
        }));
        dispatch({ type: types.CLOSE_ALL_MODALS });
        const axiosOptions = {
            url: API.inventory.deleteInstall,
            method: 'post',
            data: { id: installBaseID }
        };
        dispatch({
            type: types.INSTALL_DELETE,
            installBaseID,
            workOrders: optimisticallyDeletedWorkOrders,
            meta: {
                offline: {
                    effect: { axiosOptions, message: 'delete install' },
                    rollback: {
                        type: types.INSTALL_UPDATE,
                        install: { ...originalInstallBase, isDeleted: false },
                        workOrders: workOrdersForDeletedInstall
                    }
                }
            }
        });
        document.dispatchEvent(new CustomEvent('updatedInstallBases'));
    };
}

export function getCommissioningInstallBases(jobID: string): ThunkResult<any> {
    return (dispatch, getState) => {
        const url = `${API.job.getinstallbases}/${jobID}`;
        const axiosOptions: AxiosRequestConfig = {
            method: 'get',
            url
        };

        dispatch({
            type: types.GET_COMMISSIONING_INSTALL_BASES,
            meta: {
                offline: {
                    effect: { axiosOptions, message: 'Get Commissioning Install Bases' },
                    commit: {
                        type: types.GET_COMMISSIONING_INSTALL_BASES_SUCCESS
                    }
                }
            }
        });
    };
}

/*
 *   This is the same as addCommissioningInstallBases, except it accepts the exact values instead of looking up IDs in the store
 *   Don't want to break other functionality by updating the other function
 */
export const addCommissioningInstallBasesToJob = (jobId: string, installBaseIds: string[]): ThunkResult<any> => (
    dispatch,
    getState
) => {
    const data: JobNewInstallBases = {
        jobInstallBases: installBaseIds.map(installId => ({
            jobID: jobId,
            installbaseID: installId
        }))
    };
    const axiosOptions: AxiosRequestConfig = {
        method: 'post',
        data
    };
    const url = API.job.addinstallbases;

    return msalFetch(url, axiosOptions)
        .then((response: AxiosResponse<any>) => {
            if (!response.data) {
                throw new Error('missing data');
            } else {
                dispatch({
                    type: types.ADD_COMMISSIONING_INSTALL_BASES,
                    installBases: response.data.jobInstallBases
                });
                toastr.success('Success', 'Saved Assets to Job', constants.toastrSuccess);

                return response;
            }
        })
        .catch((error: any) => {
            dispatch({
                type: types.ADD_COMMISSIONING_INSTALL_BASES_FAILED,
                error,
                axiosOptions
            });
            constants.handleError(error, 'add commissioning install bases');
            console.error('[addCommissioningInstallBases]:', error);
        });
};

export const addCommissioningInstallBases = (job: Ijob): ThunkResult<any> => (dispatch, getState) => {
    const selectedInstallBaseIDs = selectSelectedInstallBaseIDs(getState());
    const data: JobNewInstallBases = {
        jobInstallBases: selectedInstallBaseIDs
            .filter((id): id is string => id !== undefined)
            .map(id => ({
                jobID: job.id,
                installbaseID: id
            }))
    };
    const axiosOptions: AxiosRequestConfig = {
        method: 'post',
        data
    };
    const url = API.job.addinstallbases;

    return msalFetch(url, axiosOptions)
        .then((response: AxiosResponse<any>) => {
            if (!response.data) {
                throw new Error('missing data');
            } else {
                dispatch({
                    type: types.ADD_COMMISSIONING_INSTALL_BASES,
                    installBases: response.data.jobInstallBases
                });
                toastr.success('Success', 'Saved Assets to Job', constants.toastrSuccess);

                return response;
            }
        })
        .catch((error: any) => {
            dispatch({
                type: types.ADD_COMMISSIONING_INSTALL_BASES_FAILED,
                error,
                axiosOptions
            });
            constants.handleError(error, 'add commissioning install bases');
            console.error('[addCommissioningInstallBases]:', error);
        });
};

export const saveCommissioningData = (data: IcommissioningDataPayload): ThunkResult<any> => (dispatch, getState) => {
    const axiosOptions: AxiosRequestConfig = {
        method: 'post',
        data
    };
    const url = API.POST.commissioningForm;

    return msalFetch(url, axiosOptions)
        .then((response: AxiosResponse<IcommissioningData>) => {
            if (!response.data) {
                throw new Error('missing data');
            } else {
                dispatch({
                    type: types.SAVE_COMMISSIONING_DATA,
                    commissioningData: response.data
                });
                toastr.success('Success', 'Saved Commissioning Data', constants.toastrSuccess);

                return;
            }
        })
        .catch((error: any) => {
            dispatch({
                type: types.SAVE_COMMISSIONING_DATA,
                error,
                axiosOptions
            });
            constants.handleError(error, 'save commissioning data');
            console.error('[saveCommissioningData]:', error);
        });
};

/*
 * logic taken from 'getAllProducts' event without using background worker
 * should only be called when we want to look for products after it's already been loaded once via login
 */
export const ensureLatestProducts = (trigger = 'job-open'): ThunkResult<any> => async (dispatch, getState) => {
    dispatch({
        type: types.UPDATE_INITIAL_APP_SYNC_ITEM_STATUS,
        name: appSyncItemNames.Products,
        status: AppSyncItemStatus.inProgress
    });

    let updateDate: string | undefined | null = await localforage.getItem('med-gas-mobile_all-products_delta');
    if (!updateDate) {
        console.log('[ENSURE_LATEST_PRODUCTS] [med-gas-mobile_all-products_delta] not found');
        dispatch({ type: types.REMOVE_INITIAL_APP_SYNC_ITEM, name: appSyncItemNames.Products });
        return Promise.resolve(true);
    }
    const url = `${API.material.stream}?since=${encodeURIComponent(updateDate)}`;
    const axiosOptions: AxiosRequestConfig = {
        url,
        method: 'get'
    };
    try {
        if (!axiosOptions.url) {
            throw new Error('axiosOptions.url is undefined');
        }
        // MMG-4461: same trail as the full download - a "since" response can be very large after a big SAP load
        await beginCatalogDownload({
            mode: 'delta',
            trigger,
            numChunks: 1,
            hadDeltaMarker: true,
            since: updateDate,
            userEmail: getState().user?.email
        });
        markCatalogStage('delta', 'delta', 'requested');
        const resp = await msalFetch(axiosOptions.url, axiosOptions);
        const responseData = resp.data.result ? resp.data.result : resp.data;
        markCatalogStage('delta', 'delta', 'received', {
            items: Array.isArray(responseData) ? responseData.length : undefined,
            responseChars: responseChars(resp)
        });

        const rawMaterials = normalizeMaterialResponse(responseData);
        const allMaterials: IMaterial[] = rawMaterials.map(dto => streamDtoToMaterial(dto));
        markCatalogStage('delta', 'delta', 'parsed', { items: allMaterials.length });

        if (allMaterials.length > 0) {
            const productCount = allMaterials.filter(m => m.type === 1).length;
            console.log(`[ENSURE_LATEST_PRODUCTS] [new materials] ${allMaterials.length} (${productCount} products)`);

            dispatch({
                type: types.UPDATE_SYNC_ITEM_RECORD_COUNT,
                name: appSyncItemNames.Products,
                recordCount: allMaterials.length
            });

            dispatch({
                type: types.UPDATE_SYNC_ITEM_PROGRESS,
                name: appSyncItemNames.Products,
                recordsProcessed: 0,
                recordCount: allMaterials.length
            });

            return saveMaterialsWithProgressTracking(allMaterials, (processed, total) => {
                dispatch({
                    type: types.UPDATE_SYNC_ITEM_PROGRESS,
                    name: appSyncItemNames.Products,
                    recordsProcessed: processed,
                    recordCount: total
                });
            }).then(async () => {
                await markCatalogStage('delta', 'delta', 'stored', { items: allMaterials.length });
                completeCatalogDownload('delta');
                dispatch({
                    type: types.UPDATE_SYNC_ITEM_PROGRESS,
                    name: appSyncItemNames.Products,
                    recordsProcessed: allMaterials.length,
                    recordCount: allMaterials.length
                });
                await dispatch(updateVisibleProducts());
            });
        } else {
            completeCatalogDownload('delta');
            dispatch({
                type: types.UPDATE_SYNC_ITEM_RECORD_COUNT,
                name: appSyncItemNames.Products,
                recordCount: 0
            });
        }
    } catch (error) {
        await markCatalogStage('delta', 'delta', 'failed', { error: String((error as any)?.message || error).slice(0, 500) });
        await markCatalogDownloadFailed('delta', error);
        constants.handleError(error, 'Failed to ensure latest products', 'error', true);
        console.error('[ENSURE_LATEST_PRODUCTS]:', error);
    } finally {
        dispatch({ type: types.REMOVE_INITIAL_APP_SYNC_ITEM, name: appSyncItemNames.Products });
    }
};

/*
 * installContact or contact support
 */
export function installContact(installBase: IinstallBase, message: string): ThunkResult<any> {
    return (dispatch, getState) => {
        const axiosOptions = {
            url: API.inventory.installContact,
            method: 'post',
            data: {
                facilityID: installBase.facilityID,
                installBaseID: installBase.id,
                message,
                beaconContactType: beaconContactTypeEnum.fse
            }
        };
        dispatch({
            type: types.INSTALL_CONTACT,
            installBaseID: installBase.id,
            meta: {
                offline: {
                    effect: { axiosOptions, message: 'contact support' }
                    // does contacting support need rollback support?
                }
            }
        });
        toastr.success('Success', 'Successfully contacted support.', constants.toastrSuccess);
    };
}

export function setSelectedProductByID(id: string): ThunkResult<any> {
    return (dispatch, getState) => {
        return getProductByID(id).then(products => {
            const foundProducts = filter(products, { id });

            dispatch({
                type: types.SET_SELECTED_PRODUCT,
                product: foundProducts[0] || initialProduct
            });
        });
    };
}

export const updateProductSearchFormValues = (formValues: { [key: string]: any }) => ({
    type: types.UPDATE_FORM_VALUES_MANAGE_INVENTORY_PRODUCT_SEARCH,
    formValues
});

export const setProductSearchFormValues = (formValues: { [key: string]: any }) => ({
    type: types.SET_FORM_VALUES_MANAGE_INVENTORY_PRODUCT_SEARCH,
    formValues
});

export const updateInstallFormValues = (formValues: { [key: string]: any }) => ({
    type: types.UPDATE_FORM_VALUES_MANAGE_INVENTORY_INSTALL,
    formValues
});

export const setInstallFormValues = (formValues: { [key: string]: any }) => ({
    type: types.SET_FORM_VALUES_MANAGE_INVENTORY_INSTALL,
    formValues
});

export const setDecommissionAsset = (id: string, decommissionAsset: boolean) => ({
    type: types.SET_DECOMMISSION_ASSET,
    payload: { id, decommissionAsset }
});

export const updateJobDefaultsFormValue = (formValues: { [key: string]: any }) => ({
    type: types.UPDATE_FORM_VALUES_EDIT_JOB_DEFAULTS,
    formValues
});

export const setJobDefaultsFormValues = (formValues: { [key: string]: any }) => ({
    type: types.SET_FORM_VALUES_EDIT_JOB_DEFAULTS,
    formValues
});

export const toggleAddHoursModal = () => ({
    type: types.TOGGLE_MODAL_ADD_HOURS
});

export const hideAddHoursModal = () => ({
    type: types.HIDE_MODAL_ADD_HOURS
});

export const toggleEditProductModal = () => ({
    type: types.TOGGLE_MODAL_EDIT_PRODUCT
});

export const toggleEditInstallModal = () => ({
    type: types.TOGGLE_MODAL_EDIT_INSTALL
});

export const toggleEditJobDefaultsModal = () => ({
    type: types.TOGGLE_MODAL_EDIT_JOB_DEFAULTS
});

export const toggleCommissioningDataFormModal = () => ({
    type: types.TOGGLE_MODAL_COMMISSIONING_DATA_FORM
});

export const toggleEditQuoteModal = () => ({
    type: types.TOGGLE_MODAL_EDIT_QUOTE
});

export const toggleSearchNewProductsModal = () => ({
    type: types.TOGGLE_MODAL_SEARCH_NEW_PRODUCTS
});

export const toggleModalSignaturePad = () => ({
    type: types.TOGGLE_MODAL_SIGNATURE_PAD
});

export const toggleModalNotePad = () => ({
    type: types.TOGGLE_MODAL_NOTE_PAD
});

export const setTableFilter = (filters: ItableFiltersParams): ThunkResult<any> => (dispatch, getState) => {
    const state = getState();
    const currentJobId = state.manageJob.selectedJob.id;
    
    const filtersWithJobId = {
        ...filters,
        // Always include the current job ID (unless explicitly overridden)
        jobId: filters.jobId !== undefined ? filters.jobId : currentJobId
    };
    
    return dispatch({
        type: types.SET_TABLE_FILTER_MANAGE_INVENTORY,
        filters: filtersWithJobId
    });
};

export const setFilterConfig = (config: FieldConfig) => ({
    type: types.ADD_FILTER_CONFIG,
    payload: config
});

export const clearTableFilter = () => ({
    type: types.CLEAR_TABLE_FILTER_MANAGE_INVENTORY
});

export const setSelectedProduct = (product?: { id: string }) => ({
    type: types.SET_SELECTED_PRODUCT,
    product
});

export const clearSelectedProductID = () => ({
    type: types.CLEAR_SELECTED_MANAGE_INVENTORY_PRODUCT_ID
});

export const addRecentProduct = (materialID: string) => ({
    type: types.ADD_RECENT_PRODUCT,
    materialID
});

export const removeRecentProduct = (materialID: string) => ({
    type: types.REMOVE_RECENT_PRODUCT,
    materialID
});

export const setEnableInstallBatchMode = (value: boolean) => ({
    type: types.SET_INSTALL_BATCH_MODE,
    payload: value
});

export const addVisibleProduct = (product: Iproduct) => ({
    type: types.PRODUCT_UPDATES,
    products: [product]
});

export const toggleModalAddFSE = () => ({
    type: types.TOGGLE_MODAL_ADD_FSE
});

export const toggleModalEditFacilityContacts = () => ({
    type: types.TOGGLE_MODAL_EDIT_FACILITY_CONTACTS
});

export const toggleModalVirtualSapWoParts = () => ({
    type: types.TOGGLE_MODAL_VIRTUAL_SAP_WO_PARTS
});

/*
 * Reset the products and switch to recent products
 */

export const resetNewProducts = (): ThunkResult<any> => {
    return (dispatch, getState) => {
        const { recentProducts } = getState().manageInventory;
        getAllProductsFromStore()
            .then((productsObj: { [key: string]: Iproduct }) => {
                const products = Object.values(productsObj);
                const productsById = keyBy(products, 'id');
                const sortedProducts = keyBy(
                    recentProducts
                        .map(productID => {
                            const product = productsById[productID];
                            if (!product) {
                                console.error('[resetNewProducts]: did not find product in all products');
                            }
                            return product;
                        })
                        .filter(product => product),
                    'id'
                );
                dispatch({
                    type: types.NEW_PRODUCTS_RESET,
                    products: sortedProducts
                });
            })
            .catch((error: any) => console.error('[resetNewProducts]:', error));
    };
};

export const selectProductToAdd = (newProduct: Iproduct): ThunkResult<any> => {
    return (dispatch, getState) => {
        dispatch(setSelectedProduct(newProduct));
        dispatch(addRecentProduct(newProduct.id));
        dispatch(toggleEditInstallModal());
    };
};

export const updateInstallBaseSelection = (selection: string[]) => ({
    type: types.INSTALL_UPDATE_SELECTION,
    payload: selection
});

/*
 * product names are generated by: brand, category, subcategory, type, power, model, standard, brand, SKU
 */
export const createProductName = ({
    brandID,
    mainCategoryID,
    subcategoryID,
    productTypeID,
    powerID,
    systemSizeID,
    sku
}: {
    brandID: Ioption | undefined;
    mainCategoryID: Ioption | undefined;
    subcategoryID: Ioption | undefined;
    productTypeID: Ioption | undefined;
    powerID: Ioption | undefined;
    systemSizeID: Ioption | undefined;
    sku: string | undefined;
}) => {
    const category = mainCategoryID && mainCategoryID.label !== 'N/A' ? `${mainCategoryID.label}` : '';
    const subcategory = subcategoryID && subcategoryID.label !== 'N/A' ? `: ${subcategoryID.label}` : '';
    const productType = productTypeID && productTypeID.label !== 'N/A' ? `: ${productTypeID.label}` : '';
    const power = powerID && powerID.label !== 'N/A' ? `: ${powerID.label}` : '';
    const systemSize = systemSizeID && systemSizeID.label !== 'N/A' ? `: ${systemSizeID.label}` : '';
    const skuString = sku && sku.length ? `: ${sku}` : '';
    const brand = brandID && brandID.label !== 'N/A' ? `: ${brandID.label}` : '';
    const name = `${category}${subcategory}${productType}${power}${systemSize}${brand}${skuString}`;
    if (name.length > 250) {
        return name.substr(0, 250);
    } else {
        return name;
    }
};
